import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Swiper from "swiper/bundle";
import "swiper/css/bundle";
import CartManager from "./cartManager.js";
import { showToast } from "./toast.js";

export default function CompareCard({ product, setProductCodes, checkoutEnabled = true }) {
    const nonBreakingSpace = "\u00a0";
    const swiperRef = useRef(null);
    const swiperContainerRef = useRef(null);
    const bodyRef = useRef(null);
    const swiperId = product.styleCatalogCode;
    const variantTypeSingular = product.isStylesFirst ? "Style" : "Colorway";
    const variantTypePlural = product.isStylesFirst ? "Styles" : "Colorways";
    
    // a lot of the code talks about colorways, but later on styles became a variant-type.
    // if a pattern is style first then the variant type will be style, otherwise it will be colorways.

    // this isn't always 
    const mergedColorways = (() => {
        // this used to add the room scenes into the list of colorways.
        // i am lazy and leaving this.
        return (product.isStylesFirst ? product.styles : product.colorways) || [];
    })();
    
    const [selectedColorwayIndex, setSelectedColorwayIndex] = useState(null);
    const selectedColorway = selectedColorwayIndex != null ? (mergedColorways[selectedColorwayIndex] || null) : null;
    const [isSpecOpen, setIsSpecOpen] = useState(false);
    const [isSustOpen, setIsSustOpen] = useState(false);

    const specGroups = useMemo(
        () => (Array.isArray(product?.specGroups) ? product.specGroups : []),
        [product]
    );

    const findGroupByName = useCallback(
        (groupName) =>
            specGroups.find(
                (group) =>
                    typeof group?.name === "string" &&
                    group.name.trim().toLowerCase() === groupName
            ),
        [specGroups]
    );

    const overviewGroup = useMemo(() => findGroupByName("overview"), [findGroupByName]);
    const sustainabilityGroup = useMemo(
        () => findGroupByName("sustainability"),
        [findGroupByName]
    );

    const extractSpecText = (value) => {
        if (!value || value.hasValue === false) {
            return "";
        }

        const typeName = typeof value["$type"] === "string" ? value["$type"] : "";

        if (typeName === "SpecString" || typeName === "string") {
            return typeof value.value === "string" ? value.value.trim() : "";
        }

        if (typeName === "SpecLink" || typeName === "link") {
            const linkText =
                typeof value.linkText === "string" ? value.linkText.trim() : "";
            const url = typeof value.url === "string" ? value.url.trim() : "";
            return linkText || url;
        }

        return "";
    };

    const renderSpecGroup = (group, emptyMessage) => {
        const specs = Array.isArray(group?.specs) ? group.specs : [];

        const rows = specs
            .map((spec, specIndex) => {
                const specName = spec?.name?.trim();
                if (!specName) {
                    return null;
                }

                const displayValues = (Array.isArray(spec.values) ? spec.values : [])
                    .map(extractSpecText)
                    .filter(Boolean);

                if (displayValues.length === 0) {
                    return null;
                }

                return (
                    <div
                        key={`${specName}-${specIndex}`}
                        className="compare-card__accordion-row"
                    >
                        <p className="compare-card__label">{specName}</p>
                        <div className="compare-card__accordion-values">
                            {displayValues.map((text, valueIndex) => (
                                <p
                                    key={`${specName}-${valueIndex}`}
                                    className="compare-card__value compare-card__value-accordion"
                                >
                                    {text || nonBreakingSpace}
                                </p>
                            ))}
                        </div>
                    </div>
                );
            })
            .filter(Boolean);

        if (rows.length === 0) {
            return <p className="compare-card__accordion-empty">{emptyMessage}</p>;
        }

        return rows;
    };

    const handleBodyWheel = useCallback((event) => {
        const body = event.currentTarget;
        const itemsContainer = body.closest(".compare-modal__items");
        if (!itemsContainer) return;

        const horizontalIntent = event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY);
        if (horizontalIntent) {
            const delta = event.deltaX !== 0 ? event.deltaX : event.deltaY;
            itemsContainer.scrollLeft += delta;
            event.preventDefault();
            return;
        }

        const bodyIsScrollable = body.scrollHeight > body.clientHeight;
        if (!bodyIsScrollable) {
            itemsContainer.scrollLeft += event.deltaY;
            event.preventDefault();
        }
    }, []);
    
    const handleOrderSample = async () => {
        try {
            const response = await CartManager.addBestSampleToCart(selectedColorway.catalogCode, 1);
            
            if (response.success) {
                const name = `${response.patternFamilyName}: ${response.variantName}`;
                showToast(`${name} sample added to cart.`, "success");
            } else {
                console.error("Error adding sample to cart:", response);
                showToast("Sorry, there was an error adding the sample to your cart.", "error");
            }
        } catch (e) {
            console.error("Error adding sample to cart:", e);
            showToast("Sorry, there was an error adding the sample to your cart.", "error");
        }
    };

    useEffect(() => {
        const bodyElement = bodyRef.current;
        if (!bodyElement) return;

        bodyElement.addEventListener("wheel", handleBodyWheel, { passive: false });
        return () => {
            bodyElement.removeEventListener("wheel", handleBodyWheel);
        };
    }, [handleBodyWheel]);

    useEffect(() => {
        if (!swiperContainerRef.current || mergedColorways.length <= 1) return;
        if (swiperRef.current) swiperRef.current.destroy(true, true);
        swiperRef.current = new Swiper(`#color-swiper-${swiperId}`, {
            slidesPerView: 5,
            spaceBetween: 8,
            navigation: {
                nextEl: `.swiper-button-next-${swiperId}`,
                prevEl: `.swiper-button-prev-${swiperId}`,
            },
        });
        return () => {
            if (swiperRef.current) swiperRef.current.destroy(true, true);
        };
    }, [mergedColorways, swiperId]);

    return (
        <div className="compare-card">
            <img
                src={selectedColorway?.mainImage?.url || product.styleMainImage?.url}
                alt={product.styleName}
                className="compare-card__image"
            />

            <div className="compare-card__body" ref={bodyRef}>
                <div className="compare-card__section">
                    <p className="compare-card__label">Style</p>
                    <p className="compare-card__value">{product.styleName || nonBreakingSpace}</p>
                </div>

                <div className="compare-card__section">
                    <p className="compare-card__label">Collection</p>
                    <p className="compare-card__value">{product.collectionName || nonBreakingSpace}</p>
                </div>

                <div className="compare-card__section">
                    <p className="compare-card__label">Style Number</p>
                    <p className="compare-card__value">{product.styleNumber || nonBreakingSpace}</p>
                </div>

                <div className="compare-card__section">
                    <p className="compare-card__label">{variantTypeSingular}</p>
                    <p className="compare-card__value">{selectedColorway?.name || 'Select Below'}</p>
                </div>

                {mergedColorways.length > 0 && (
                    <div className="compare-card__colors">
                        <p className="compare-card__label">{mergedColorways.length} {variantTypePlural}</p>
                        <div className="compare-card__colors-wrapper">
                            <div className={`swiper-button-prev swiper-button-prev-${swiperId}`}></div>
                            <div
                                id={`color-swiper-${swiperId}`}
                                ref={swiperContainerRef}
                                className="compare-card__color-swatches swiper"
                            >
                                <div className="swiper-wrapper">
                                    {mergedColorways.map((cw, i) => (
                                        <div
                                            key={`${product.styleCatalogCode}-color-${i}`}
                                            className={`compare-card__color-swatch swiper-slide ${i === selectedColorwayIndex ? "active" : ""}`}
                                            onClick={() => setSelectedColorwayIndex(i)}
                                        >
                                            <div
                                                className={`compare-card__color-swatch-image-wrapper ${i === selectedColorwayIndex ? "selected" : ""}`}
                                            >
                                                <img
                                                    src={cw.mainImage?.url}
                                                    alt={cw.name}
                                                    className="compare-card__color-swatch-image"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={`swiper-button-next swiper-button-next-${swiperId}`}></div>
                        </div>
                    </div>
                )}

                <div className="compare-card__accordion-wrapper">
                    <div className="compare-card__accordion">
                        <div
                            className="compare-card__accordion-header"
                            onClick={() => setIsSpecOpen(!isSpecOpen)}
                        >
                            <p>Specifications</p>
                            <img
                                src={isSpecOpen ? "/images/minus-sign.svg" : "/images/plus-sign.svg"}
                                alt={isSpecOpen ? "Collapse" : "Expand"}
                            />
                        </div>
                        {isSpecOpen && (
                            <div className="compare-card__accordion-body">
                                {renderSpecGroup(
                                    overviewGroup,
                                    "Specifications not available."
                                )}
                            </div>
                        )}
                    </div>
                    <div className="compare-card__accordion">
                        <div
                            className="compare-card__accordion-header"
                            onClick={() => setIsSustOpen(!isSustOpen)}
                        >
                            <p>Sustainability</p>
                            <img
                                src={isSustOpen ? "/images/minus-sign.svg" : "/images/plus-sign.svg"}
                                alt={isSustOpen ? "Collapse" : "Expand"}
                            />
                        </div>
                        {isSustOpen && (
                            <div className="compare-card__accordion-body">
                                {renderSpecGroup(
                                    sustainabilityGroup,
                                    "Sustainability information not available."
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="compare-card__footer">
                {checkoutEnabled && (
                    <button className="btn" disabled={selectedColorway === null} onClick={_ => handleOrderSample()}>
                        Order Sample
                    </button>
                )}
                
                <button
                    className="btn-secondary"
                    onClick={() => {
                        setProductCodes(prev => prev.filter(c => c !== product.styleCatalogCode));
                        window.dispatchEvent(
                            new CustomEvent("compare-toggle", {
                                detail: {
                                    code: product.styleCatalogCode,
                                    img: "",
                                    checked: false,
                                },
                            })
                        );
                    }}
                >
                    Remove Style
                </button>
            </div>
        </div>
    );
}
